import React from 'react'
import {
    Box,
    Container,
    SimpleGrid,
    Stack,
    Heading,
    Text,
    Button,
    Image,
    useColorModeValue,
    chakra,
    Icon
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FiActivity, FiTarget } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'

// corrected import paths
import actionsImg from '../../assets/actions.png'
import dailyLogImg from '../../assets/daily_log.png'
import journalImg from '../../assets/food_journal.png'
import breakdownImg from '../../assets/meal_breakdown.png'

const MotionBox = motion(chakra.div)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)

export default function FeaturesPage() {
    const { t } = useTranslation()
    const pageBg = useColorModeValue('gray.50', 'gray.900')
    const heroBg = useColorModeValue(
        'linear(to-r, teal.400, blue.500)',
        'linear(to-r, teal.700, blue.900)'
    )
    const textColor = useColorModeValue('gray.50', 'gray.100')
    const cardBg = useColorModeValue('white', 'gray.700')
    const accent = useColorModeValue('teal.300', 'blue.300')
    const descColor = useColorModeValue('gray.600', 'gray.300')
    const imgColor = useColorModeValue('gray.50', 'gray.800')
    const titleColor = useColorModeValue('gray.800', 'white')

    const steps = [
        {
            image: actionsImg,
            alt: t('features.steps.instantAlt'),
            title: t('features.steps.instant.title'),
            desc: t('features.steps.instant.desc'),
            bg: cardBg
        },
        {
            image: dailyLogImg,
            alt: t('features.steps.dailyAlt'),
            title: t('features.steps.daily.title'),
            desc: t('features.steps.daily.desc'),
            bg: imgColor
        },
        {
            image: journalImg,
            alt: t('features.steps.deepAlt'),
            title: t('features.steps.deep.title'),
            desc: t('features.steps.deep.desc'),
            bg: cardBg
        },
        {
            image: breakdownImg,
            alt: t('features.steps.visualAlt'),
            title: t('features.steps.visual.title'),
            desc: t('features.steps.visual.desc'),
            bg: imgColor
        }
    ]

    const comingSoon = [
        {
            icon: FiActivity,
            title: t('features.coming.workout.title'),
            desc: t('features.coming.workout.desc')
        },
        {
            icon: FiTarget,
            title: t('features.coming.plan.title'),
            desc: t('features.coming.plan.desc')
        }
    ]

    return (
        <Box as="main" bg={pageBg}>
            {/* Hero Section */}
            <Box
                position="relative"
                bgGradient={heroBg}
                color="white"
                overflow="hidden"
                py={{ base: 24, md: 40 }}
            >
                <Container maxW="container.md" textAlign="center" position="relative" zIndex={1}>
                    <MotionHeading
                        as="h1"
                        fontSize={{ base: '4xl', md: '6xl' }}
                        fontWeight="extrabold"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        bgClip="text"
                        bgGradient="linear(to-r, whiteAlpha.900, whiteAlpha.700)"
                    >
                        {t('features.hero.title')}
                    </MotionHeading>

                    <MotionText
                        fontSize={{ base: 'md', md: 'lg' }}
                        mt={4}
                        mb={8}
                        color={textColor}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        {t('features.hero.subtitle')}
                    </MotionText>

                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} justify="center">
                        <Button
                            as={Link}
                            to="/register"
                            size="lg"
                            bg="white"
                            color="blue.600"
                            fontWeight="bold"
                            _hover={{ bg: 'whiteAlpha.800' }}
                            whileHover={{ scale: 1.03 }}
                            transition="all 0.2s"
                        >
                            {t('features.hero.button')}
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Core Features as Motion Cards */}
            {steps.map(({ image, alt, title, desc, bg }, i) => (
                <MotionBox
                    key={title}
                    bg={bg}
                    p={8}
                    rounded="2xl"
                    shadow="xl"
                    whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    py={16}
                >
                    <Container maxW="container.lg">
                        <SimpleGrid columns={[1, null, 2]} spacing={10} align="center">
                            <Image
                                src={image}
                                alt={alt}
                                mx="auto"
                                maxH="300px"
                                objectFit="contain"
                                borderRadius="md"
                                shadow="md"
                            />
                            <Stack spacing={4}>
                                <Heading size="lg" color={titleColor}>
                                    {title}
                                </Heading>
                                <Text fontSize="md" color={descColor}>
                                    {desc}
                                </Text>
                            </Stack>
                        </SimpleGrid>
                    </Container>
                </MotionBox>
            ))}

            {/* Coming Soon Section */}
            <Box py={20} bg={pageBg}>
                <Container maxW="container.lg" textAlign="center">
                    <Heading size="xl" mb={8}>
                        {t('features.coming.title')}
                    </Heading>
                    <SimpleGrid columns={[1, null, 2]} spacing={10}>
                        {comingSoon.map(({ icon, title, desc }, i) => (
                            <MotionBox
                                key={title}
                                bg={cardBg}
                                p={8}
                                rounded="2xl"
                                shadow="xl"
                                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                                textAlign="center"
                            >
                                <Icon as={icon} w={12} h={12} color={accent} mb={4} />
                                <Heading size="md">{title}</Heading>
                                <Text color={descColor}>{desc}</Text>
                            </MotionBox>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box py={20}>
                <Container maxW="container.md" textAlign="center">
                    <Heading size="xl" mb={4}>
                        {t('features.cta.title')}
                    </Heading>
                    <Button
                        as={Link}
                        to="/register"
                        size="lg"
                        bg="blue.600"
                        color="white"
                        _hover={{ bg: 'blue.500' }}
                    >
                        {t('features.cta.button')}
                    </Button>
                </Container>
            </Box>
        </Box>
    )
}